import { useState, useEffect } from "react";
import { supabase } from "./lib/supabase";
import Login from "./components/Login";
import Sidebar from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import Gastos from "./components/Gastos";
import BalanceGeneral from "./components/BalanceGeneral";
import LibroMayor from "./components/LibroMayor";
import Ingresos from "./components/Ingresos";
import F29 from "./components/f29";
import Cotizaciones from "./components/Cotizaciones";
import Proyectos from "./components/Proyectos";
import CotizacionPublica from "./components/CotizacionPublica";
import ProyectoPublico from "./components/ProyectoPublico";
import PortalCliente from "./components/PortalCliente";
import { Bell } from "lucide-react";

const PAGE_TITLES = {
  dashboard:    "Dashboard",
  ingresos:     "Ingresos",
  gastos:       "Gastos",
  iva:          "IVA / F29",
  libro:        "Libro Mayor",
  balance:      "Balance General",
  cotizaciones: "Cotizaciones",
  proyectos:    "Proyectos",
};

// ── Detectar tokens públicos FUERA del componente
// ?token=   → página pública de cotización
// ?ptoken=  → página pública de proyecto
const PUBLIC_TOKEN  = new URLSearchParams(window.location.search).get("token");
const PROJECT_TOKEN = new URLSearchParams(window.location.search).get("ptoken");
const CLIENT_TOKEN  = new URLSearchParams(window.location.search).get("ctoken");
const IS_PUBLIC     = !!(PUBLIC_TOKEN || PROJECT_TOKEN || CLIENT_TOKEN);
// ?proyecto=PRY-…&tarea=uuid → enlace directo a la conversación (notificaciones)
const URL_PARAMS     = new URLSearchParams(window.location.search);
const LINK_PROYECTO  = URL_PARAMS.get("proyecto")
  ? { proyecto: URL_PARAMS.get("proyecto"), tarea: URL_PARAMS.get("tarea") || null }
  : null;
export default function App() {
  // ── Rutas públicas — ANTES de cualquier hook
  if (window.location.pathname === "/portal") return <PortalCliente />;
  if (PUBLIC_TOKEN)  return <CotizacionPublica />;
  if (PROJECT_TOKEN) return <ProyectoPublico />;
  if (CLIENT_TOKEN)  return <PortalCliente />;

  return <AppInterna />;
}

function AppInterna() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(LINK_PROYECTO ? "proyectos" : "dashboard");
  // El enlace directo se usa una sola vez: al navegar a otra sección se descarta.
  const [linkInicial, setLinkInicial] = useState(LINK_PROYECTO);
  const navegar = (p) => { setLinkInicial(null); setPage(p); };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => setSession(session)
    );

    return () => subscription.unsubscribe();
  }, []);

  // El panel es solo para administradores. Los clientes del portal y los
  // usuarios de otras apps del mismo proyecto Supabase también tienen sesión:
  // se les muestra un aviso en vez del panel (los datos los protege además el RLS).
  const [rol, setRol] = useState("cargando"); // cargando | admin | sin_acceso | error
  const uid = session?.user?.id;
  useEffect(() => {
    if (!uid) return;
    let vivo = true;
    setRol("cargando");
    supabase.from("user_roles").select("role").eq("user_id", uid).maybeSingle()
      .then(({ data, error }) => {
        if (!vivo) return;
        if (error) setRol("error");
        else setRol(data?.role === "admin" ? "admin" : "sin_acceso");
      });
    return () => { vivo = false; };
  }, [uid]);

  async function handleLogout() {
    await supabase.auth.signOut();
  }

  function renderPage() {
    switch (page) {
      case "dashboard":    return <Dashboard />;
      case "ingresos":     return <Ingresos />;
      case "libro":        return <LibroMayor />;
      case "balance":      return <BalanceGeneral />;
      case "gastos":       return <Gastos />;
      case "iva":          return <F29 />;
      case "cotizaciones": return <Cotizaciones />;
      case "proyectos":    return <Proyectos inicial={linkInicial} />;
      default: return (
        <div style={{ padding:40 }}>
          <h1 style={{ color:"#0d1117", fontSize:22, fontWeight:700, marginBottom:8 }}>
            {PAGE_TITLES[page] || page}
          </h1>
          <p style={{ color:"#94a3b8", fontSize:14 }}>Módulo en construcción...</p>
        </div>
      );
    }
  }

  if (loading) return (
    <div style={{ minHeight:"100vh", background:"#ffffff", display:"flex",
      alignItems:"center", justifyContent:"center" }}>
      <div style={{ color:"#29abe2", fontSize:14, letterSpacing:2 }}>Cargando...</div>
    </div>
  );

  if (!session) return <Login />;

  if (rol !== "admin") return (
    <div style={{ minHeight:"100vh", background:"#f8fafc", display:"flex", alignItems:"center",
      justifyContent:"center", padding:24, fontFamily:"'DM Sans', sans-serif" }}>
      {rol === "cargando" ? (
        <div style={{ color:"#29abe2", fontSize:14, letterSpacing:2 }}>Cargando...</div>
      ) : (
        <div role="alert" style={{ maxWidth:420, background:"#fff", border:"1px solid #e2e8f0",
          borderRadius:14, padding:"28px 26px", textAlign:"center" }}>
          <div style={{ fontWeight:700, fontSize:16, marginBottom:8, color:"#1e293b" }}>
            {rol === "error" ? "No se pudo verificar tu acceso" : "Esta cuenta no tiene acceso al panel administrativo"}
          </div>
          <p style={{ fontSize:13, color:"#64748b", lineHeight:1.6, margin:"0 0 18px" }}>
            {rol === "error"
              ? "Revisa tu conexión e inténtalo nuevamente."
              : "Si eres cliente de TuAplicacion, ingresa al Portal de Clientes para ver tus proyectos."}
          </p>
          <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
            {rol === "error"
              ? <button onClick={() => window.location.reload()} style={btnPanel(true)}>Reintentar</button>
              : <a href="/portal" style={btnPanel(true)}>Ir al Portal de Clientes</a>}
            <button onClick={handleLogout} style={btnPanel(false)}>Cerrar sesión</button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display:"flex", minHeight:"100vh", background:"#f8fafc",
      fontFamily:"'DM Sans', sans-serif" }}>
      <Sidebar active={page} onNavigate={navegar} onLogout={handleLogout}
        empresa="TuAplicacion SpA" />

      <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>
        <header style={{
          height:58, background:"#ffffff", borderBottom:"1px solid #e2e8f0",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"0 28px", flexShrink:0,
        }}>
          <div>
            <h1 style={{ color:"#0d1117", fontSize:15, fontWeight:700, margin:0 }}>
              {PAGE_TITLES[page]}
            </h1>
            <p style={{ color:"#94a3b8", fontSize:11, margin:0 }}>
              Sistema Contable Chile ·{" "}
              {new Date().toLocaleDateString("es-CL", { month:"long", year:"numeric" })}
            </p>
          </div>

          <div style={{ display:"flex", alignItems:"center", gap:16 }}>
            <button style={{ background:"none", border:"none", cursor:"pointer",
              color:"#94a3b8", position:"relative", padding:4 }}>
              <Bell size={18} />
              <span style={{ position:"absolute", top:2, right:2, width:7, height:7,
                background:"#29abe2", borderRadius:"50%" }} />
            </button>
            <div style={{
              width:32, height:32, borderRadius:"50%",
              background:"#29abe215", border:"1.5px solid #29abe240",
              display:"flex", alignItems:"center", justifyContent:"center",
              color:"#29abe2", fontSize:13, fontWeight:700,
            }}>
              {session.user.email?.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        <main style={{ flex:1, overflow:"auto", padding:28 }}>
          {renderPage()}
        </main>
      </div>
    </div>
  );
}
const btnPanel = (principal) => ({
  padding:"9px 18px", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer",
  textDecoration:"none", fontFamily:"'DM Sans', sans-serif",
  border: principal ? "none" : "1px solid #e2e8f0",
  background: principal ? "#29abe2" : "transparent",
  color: principal ? "#fff" : "#64748b",
});
